<template>
  <div :class="['pf-v5-c-switchlist', $attrs.class]" :style="$attrs.style" v-bind="ouiaProps">
    <input v-if="name && !value.length" type="hidden" :name="name" value="" />

    <slot>
      <pf-switch
        v-for="o of normalizedOptions"
        :key="o.value"
        v-bind="{ ...$attrs, style: undefined, class: undefined }"
        :checked="value.includes(o.value)"
        :name="name ? `${name}[${o.value}]` : undefined"
        :value="o.value"
        :label="o.label"
        :disabled="disabled"
        @update:checked="$event ? addItem(o) : removeItem(o)"
        :ouia-id="`${ouiaProps['data-ouia-component-id']}-${o.value}`"
      />
    </slot>
  </div>
</template>

<style lang="scss">
:root {
  --pf-v5-c-switchlist--ColumnWidth: 250px;
}

.pf-v5-c-switchlist {
  column-width: var(--pf-v5-c-switchlist--ColumnWidth);

  .pf-v5-c-switch {
    margin: 1px 0;
    min-width: var(--pf-v5-c-switchlist--ColumnWidth);
  }
}
</style>

<script lang="ts">
type OptionField<Option extends Record<string, any> | string> = Option extends string ? never : keyof Option | ((option: Option) => string);

export interface PfSwitchListProps<Option extends Record<string, any> | string> extends /* @vue-ignore */ HTMLAttributes {
  name?: string;
  items?: readonly Option[] | Record<string, Option>;
  labelField?: OptionField<Option>;
  valueField?: OptionField<Option>;
  disabled?: boolean;
}
</script>

<script lang="ts" setup generic="Option extends Record<string, any> | string">
import { type HTMLAttributes, computed } from 'vue';
import { useOUIAProps, type OUIAProps } from '@common/ouia';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<OUIAProps & PfSwitchListProps<Option>>(), {
  items: () => [] as Option[],
});
const ouiaProps = useOUIAProps(props);

const value = defineModel<(string | number)[]>('modelValue', { default: [] });

function removeItem(item: NormalizedOption<Option>) {
  const i = String(value.value).indexOf(item.value);
  if (i < 0) {
    return;
  }

  const items = [...value.value];
  items.splice(i, 1);
  value.value = items;
}

function addItem(item: NormalizedOption<Option>) {
  if (!value.value.includes(item.value)) {
    value.value = [...value.value, item.value];
  }
}

type NormalizedOption<O extends Option> = {
  label: string;
  value: string;
  option: O;
};

function label(option: Option): string {
  if (typeof props.labelField === 'function') {
    return props.labelField(option as any);
  }
  return String(typeof option === 'object' ? option[props.labelField as keyof typeof option] : option);
}

const normalizedOptions = computed(() => {
  const norm: NormalizedOption<Option>[] = [];
  if (Array.isArray(props.items)) {
    for (const option of props.items) {
      const isObj = typeof option === 'object';
      const value = isObj ? option[props.valueField as keyof typeof option] : option;
      const labelString = label(option);
      norm.push({
        option,
        label: labelString,
        value,
      });
    }
  } else {
    for (const [value, option] of Object.entries(props.items)) {
      const labelString = label(option);
      norm.push({
        option,
        label: labelString,
        value,
      });
    }
  }
  return norm;
});
</script>
